import {
  Button,
  Card,
  Center,
  Checkbox,
  Flex,
  Group,
  Input,
  SegmentedControl,
  Stack,
  TextInput
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconBuilding, IconShieldLock, IconUser } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { Workspace, WorkspaceType } from '@/types/workspace'
import { isAlphanumericWithSpaces } from '@/utils/validation/helpers'

const workspaceTypeOptions = [
  {
    label: (
      <Center style={{ gap: 10 }}>
        <IconUser size={16} />
        <FormattedMessage id="workspace.type.personal" />
      </Center>
    ),
    value: String(WorkspaceType.Personal)
  },
  {
    label: (
      <Center style={{ gap: 10 }}>
        <IconBuilding size={16} />
        <FormattedMessage id="workspace.type.organization" />
      </Center>
    ),
    value: String(WorkspaceType.Organization)
  }
]

type FormValues = {
  name: string
  workspace_type: string
  require_mfa: boolean
}

export type SubmitValues = Omit<FormValues, 'workspace_type'> & {
  workspace_type: WorkspaceType
}

type WorkspaceSettingsFormProps = {
  workspace?: Workspace
  mode?: 'create' | 'update'
  buttonAlign?: 'start' | 'center' | 'end'
  disabled?: boolean
  onSubmit: (formValues: SubmitValues) => void
}

export const WorkspaceSettingsForm = ({
  workspace,
  mode = 'create',
  buttonAlign = 'start',
  disabled = false,
  onSubmit
}: WorkspaceSettingsFormProps) => {
  const intl = useIntl()

  const form = useForm<FormValues>({
    enhanceGetInputProps: () => {
      return {
        disabled
      }
    },
    initialValues: {
      name: workspace?.name || '',
      workspace_type: String(
        workspace?.workspace_type || WorkspaceType.Personal
      ),
      require_mfa: workspace?.require_mfa || false
    },

    validate: {
      name: (value) => {
        if (value.length === 0) {
          return intl.formatMessage({ id: 'workspace.name.required' })
        }

        if (!isAlphanumericWithSpaces(value)) {
          return intl.formatMessage({ id: 'workspace.name.invalid' })
        }

        return null
      }
    }
  })

  const handleSubmit = (formValues: FormValues) => {
    form.resetDirty()

    onSubmit({
      ...formValues,
      workspace_type: Number(formValues.workspace_type)
    })
  }

  return (
    <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xl">
        {disabled && (
          <PlatformAlert variant="info">
            <FormattedMessage id="workspace.settings.disabled.description" />
          </PlatformAlert>
        )}

        <TextInput
          label={<FormattedMessage id="workspace.name" />}
          {...form.getInputProps('name')}
        />

        <Input.Wrapper label={<FormattedMessage id="workspace.type" />}>
          <div>
            <SegmentedControl
              miw={320}
              {...form.getInputProps('workspace_type')}
              data={workspaceTypeOptions}
            />
          </div>
        </Input.Wrapper>

        {mode === 'update' && (
          <Card bg="gray.1">
            <Checkbox
              label={
                <Group gap="xs" wrap="nowrap" align="center">
                  <FormattedMessage id="workspace.mfa.toggle.label" />

                  <IconShieldLock
                    size={20}
                    color="var(--mantine-color-yellow-8)"
                  />
                </Group>
              }
              description={
                <FormattedMessage id="workspace.mfa.toggle.description" />
              }
              {...form.getInputProps('require_mfa', { type: 'checkbox' })}
            />
          </Card>
        )}

        <Flex justify={buttonAlign}>
          <Button
            type="submit"
            disabled={(mode === 'update' && !form.isDirty()) || disabled}
          >
            <FormattedMessage
              id={mode === 'create' ? 'workspace.create' : 'workspace.update'}
            />
          </Button>
        </Flex>
      </Stack>
    </form>
  )
}
